/**
 * Bootstrap and wiring. The only module that touches the document.
 *
 * Deliberately boring: read state, render, attach delegated listeners once. Every mutation goes
 * through `state.ts` and ends in a `render()` call, so what is on screen is always a pure
 * function of state — there is no path where a finding from a previous PSBT survives next to a
 * fresh intent panel.
 */
import '@signingroom/ui/styles.css';
import { $, $$, mount, on, html, raw } from '@signingroom/ui/dom';
import {
  state, loadVault, parseVault, applyVault, forgetVault, analyse, clearLoaded,
  addContribution, finalizeNow, panelsToPreserve, clearQr, switchQrFormat, scanWindow,
  type PanelSettled,
} from './state.js';
import { controlView } from './control-view.js';
import {
  issueChallenge, messageToSign, readChallenge, assembleProof, proofToJson, checkProof,
} from './control.js';
import { vaultView, loadView, reviewView, assembleView, qrOutView,
         headView, railView, capabilityView } from './views.js';
import { decodePsbtInput, readFile, download, toBase64, toHex } from './psbt-io.js';
import { encodeFrames, frameToSvg, Scanner, makeDetector, hasNativeDetector,
         type Format } from './qr.js';

const app = $('#app')!;

/**
 * Re-render the whole app, preserving the two pieces of state that live in the DOM rather than
 * in `state`: what has focus, and which <details> the user opened.
 *
 * Replacing innerHTML is what makes "the screen is a pure function of state" true, and that
 * property is worth keeping on a signing surface — it is why a finding from a previous PSBT
 * cannot survive next to a fresh intent panel. But done naively it throws a keyboard user back
 * to the top of the document on every acknowledgement tick, and collapses any finding they had
 * expanded. On a screen where you must tick several boxes before you may sign, that is not a
 * cosmetic problem. Observed in the browser, not theorised.
 */
/** Whether each step panel was settled at the previous render. See `panelsToPreserve`. */
let panelSettled: PanelSettled = { vault: false, load: false };

function render(): void {
  const active = document.activeElement as HTMLElement | null;
  const focusKey = active?.dataset.ack ? `[data-ack="${active.dataset.ack}"]`
                 : active?.id ? `#${active.id}` : null;
  const caret = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement
    ? { start: active.selectionStart, end: active.selectionEnd } : null;
  const opened = $$('details[data-code]').filter((d) => (d as HTMLDetailsElement).open)
    .map((d) => d.dataset.code!);
  /*
   * A step panel collapses exactly ONCE — on the render where it becomes settled — and after
   * that the user's own open/closed choice is carried across re-renders.
   *
   * Neither half alone works. Collapse every render and typing in the scan window shuts the
   * panel you are typing into. Preserve every render and it never collapses at all, because
   * the panel was open right up to the moment it settled. So: preserve, EXCEPT on the
   * transition.
   */
  const settledNow = { vault: !!state.parsed && !state.vaultError, load: !!state.loaded };
  const SEL = { vault: '#vault-card', load: '#load-card' } as const;
  const panels = panelsToPreserve(settledNow, panelSettled,
    (k) => !!($(SEL[k]) as HTMLDetailsElement | null)?.open).map((k) => SEL[k]);
  panelSettled = settledNow;

  /*
   * Two columns: the working sequence on the left, reference material in a sticky rail.
   *
   * As one column this page ran to 3,348px with a transaction loaded — the vault's policy and
   * the two hashes had scrolled a full screen away from the findings that are read against
   * them. `.cols-wide` is the house grid (1.35fr / 1fr) and collapses to one column below 820px.
   */
  /*
   * Two screens, one page. The inspector answers "what does this payment do"; the control screen
   * answers "is this wallet yours". They share the vault descriptor and nothing else, so they are
   * siblings rather than steps — a proof of control is not part of approving a payment.
   */
  mount(app, html`
    ${state.tab === 'control' ? html`
      ${controlView()}
      ${/*
         * The wallet panel belongs on THIS screen too, not only on the inspector.
         *
         * Proof of control is now the front door, and `proveView` told a first-time visitor to
         * "open Your wallet above" — a panel that is not rendered on this tab. You cannot prove
         * control of a wallet you have not named, so the naming has to be here.
         */''}
      ${/*
          * ONLY in prove mode. The check screen says in its own first line "You need nothing of
          * your own — no wallet, no account, no descriptor", and rendering a wallet setup panel
          * underneath that made the page contradict itself in the space of one scroll. Checking
          * a proof genuinely needs nothing: the bundle carries the wallet it is about.
          */''}
      ${state.control.mode === 'prove' ? vaultView(opened) : raw('')}` : html`
      ${headView()}
      <div class="cols-wide" style="margin-top:22px">
        <div>
          ${vaultView(opened)}${loadView()}${reviewView(opened)}${assembleView()}${qrOutView()}
          ${capabilityView()}
        </div>
        ${railView()}
      </div>`}`);
  paintQr();
  // A panel is open if the user had it open (and this is not its collapse transition), or if
  // its step is not settled yet and there is still something to fill in.
  for (const k of ['vault', 'load'] as const) {
    const el = $(SEL[k]) as HTMLDetailsElement | null;
    if (el) el.open = panels.includes(SEL[k]) || !settledNow[k];
  }

  if (focusKey) {
    const restored = $(focusKey);
    if (restored) {
      restored.focus({ preventScroll: true });
      if (caret && (restored instanceof HTMLInputElement || restored instanceof HTMLTextAreaElement)) {
        try { restored.setSelectionRange(caret.start, caret.end); } catch { /* type has no caret */ }
      }
    }
  }
}

/* ── vault ────────────────────────────────────────────────────────────────── */

function readVaultInputs(): void {
  const num = (id: string, fallback: number) => {
    const v = Number(($(`#${id}`) as HTMLInputElement | null)?.value);
    return Number.isFinite(v) && v >= 0 ? Math.floor(v) : fallback;
  };
  state.vault.descriptor = ($('#descriptor') as HTMLTextAreaElement | null)?.value ?? '';
  state.vault.lastUsedReceive = num('lastUsedReceive', state.vault.lastUsedReceive);
  state.vault.lastUsedChange = num('lastUsedChange', state.vault.lastUsedChange);
  state.vault.gapLimit = Math.max(1, num('gapLimit', state.vault.gapLimit));
}

/* ── loading a PSBT ───────────────────────────────────────────────────────── */

function inspect(input: Uint8Array | string): void {
  try {
    analyse(decodePsbtInput(input));
  } catch (e) {
    state.loaded = null;
    state.loadError = (e as Error).message;
  }
  render();
  // Findings are the reason the user is here; scroll them into view rather than leaving the
  // verdict below the fold on a laptop.
  if (state.loaded) $('#review')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/* ── events (delegated once, survive re-render) ───────────────────────────── */

/**
 * A working wallet to try, for anyone evaluating this without one of their own.
 *
 * SINGLE-KEY, because Solo is the single-key product — an example this build then refuses would
 * be worse than no example at all. Testnet, so the "Real bitcoin / Test coins" badge shows the
 * safe value, and deterministic so the same key appears every time. `test/zz-audit-solo` asserts
 * this string still parses under the set Solo actually uses.
 */
const EXAMPLE_DESCRIPTOR =
  'wpkh([63969fb2/84h/1h/0h]' +
  'tpubDCn3Cs4qLCeLcfbcbBs4sXiHbxnQ77EDejkRb4aB4FPLiF8BC6uEzmY7UWHwxcmcQn2g7aPWjFjmU1cwbGeMtLvsCnou4TVWxjkoosLHUqo' +
  '/<0;1>/*)#lnufs763';

on(app, 'click', '#load-example', () => {
  const box = $('#descriptor') as HTMLTextAreaElement | null;
  if (box) box.value = EXAMPLE_DESCRIPTOR;
  readVaultInputs();
  applyVault();
  render();
});

on(app, 'click', '#apply-vault', () => { readVaultInputs(); applyVault(); render(); });
on(app, 'click', '#forget-vault', () => { forgetVault(); render(); });
on(app, 'click', '#clear', () => { clearLoaded(); render(); });

on(app, 'click', '#inspect', () => {
  const text = ($('#paste') as HTMLTextAreaElement | null)?.value ?? '';
  const file = ($('#file') as HTMLInputElement | null)?.files?.[0];
  if (file) { void readFile(file).then(inspect); return; }
  inspect(text);
});

on(app, 'change', '#file', (el) => {
  const file = (el as HTMLInputElement).files?.[0];
  if (file) void readFile(file).then(inspect);
});

on(app, 'change', 'input[data-ack]', (el) => {
  const code = el.dataset.ack!;
  if ((el as HTMLInputElement).checked) state.acknowledged.add(code);
  else state.acknowledged.delete(code);
  render();
});

// Drag and drop, because a .psbt lands in a downloads folder and gets dragged from there.
const stop = (e: Event) => { e.preventDefault(); e.stopPropagation(); };
for (const type of ['dragenter', 'dragover', 'dragleave', 'drop'] as const) {
  app.addEventListener(type, stop);
}
app.addEventListener('drop', (e) => {
  const file = (e as DragEvent).dataTransfer?.files?.[0];
  if (file) void readFile(file).then(inspect);
});

/* ── assembly ─────────────────────────────────────────────────────────────── */

function addSig(input: Uint8Array | string, label: string): void {
  try {
    addContribution(decodePsbtInput(input), label);
  } catch (e) {
    state.assembly.error = (e as Error).message;
  }
  render();
}

on(app, 'click', '#add-sig', () => {
  const file = ($('#sig-file') as HTMLInputElement | null)?.files?.[0];
  if (file) { void readFile(file).then((b) => addSig(b, file.name)); return; }
  const text = ($('#sig-paste') as HTMLTextAreaElement | null)?.value ?? '';
  addSig(text, 'pasted');
});
on(app, 'change', '#sig-file', (el) => {
  const file = (el as HTMLInputElement).files?.[0];
  if (file) void readFile(file).then((b) => addSig(b, file.name));
});
on(app, 'click', '#finalize', () => { finalizeNow(); render(); });

on(app, 'click', '[data-export]', (el) => {
  const A = state.assembly;
  const stem = A.extracted ? A.extracted.txid.slice(0, 12) : 'signingroom';
  switch (el.dataset.export) {
    /*
     * Hand the UNSIGNED PSBT to a software wallet.
     *
     * Every transport in step 4 pointed INWARD — paste a signature, drop a file, scan a QR —
     * and the only way out was an animated QR aimed at an air-gapped device. So a Sparrow or
     * Bitcoin Core user could load, review and acknowledge a transaction here and then had no
     * route to their signer except retyping the base64 they started from. The bytes offered are
     * the exact bytes that were loaded and hashed, which is what makes the round trip checkable.
     */
    case 'tosign':
      if (state.loaded) download(state.loaded.bytes, `${stem}-to-sign.psbt`, 'application/octet-stream');
      break;
    case 'tosign64':
      if (state.loaded) void navigator.clipboard?.writeText(toBase64(state.loaded.bytes));
      break;
    case 'rawhex':
      if (A.extracted) void navigator.clipboard?.writeText(toHex(A.extracted.raw));
      break;
    case 'rawfile':
      if (A.extracted) download(toHex(A.extracted.raw), `${stem}.txn`, 'text/plain');
      break;
    case 'psbt':
      if (A.finalized) download(A.finalized, `${stem}-final.psbt`, 'application/octet-stream');
      break;
    case 'combined':
      if (A.combined) download(A.combined, `${stem}-combined.psbt`, 'application/octet-stream');
      break;
    case 'combined64':
      if (A.combined) void navigator.clipboard?.writeText(toBase64(A.combined));
      break;
  }
});

/* ── animated QR out ──────────────────────────────────────────────────────── */

/**
 * The animation writes into ONE node and never triggers a full render.
 *
 * A multi-frame PSBT advances several times a second; re-rendering the app at that rate would
 * make every other control on the page unusable while a QR is playing.
 */
let qrTimer: number | null = null;
let qrIndex = 0;

function paintQr(): void {
  if (qrTimer !== null) { clearInterval(qrTimer); qrTimer = null; }
  const host = $('#qr-frame');
  if (!host || state.qr.frames.length === 0) return;

  const draw = () => {
    const frames = state.qr.frames;
    if (frames.length === 0) return;
    qrIndex = qrIndex % frames.length;
    host.innerHTML = frameToSvg(frames[qrIndex]!);
    const idx = $('#qr-index');
    if (idx) idx.textContent = `${qrIndex + 1} / ${frames.length}`;
    qrIndex++;
  };
  draw();
  if (state.qr.frames.length > 1) {
    qrTimer = window.setInterval(draw, state.qr.interval);
  }
}

/**
 * Tear down the QR panel. ONE owner, because the frames are the dangerous part.
 *
 * A camera reads the frames on the screen; it does not read the heading above them, the format
 * button beside them, or the panel's `open` flag. So every path that stops showing a PSBT has
 * to clear `frames`, and it has to be the same path — the previous close handler cleared them
 * and the error path in `openQr` did not, which left a decoded PSBT in state under a closed
 * panel waiting to be re-rendered by the next thing that opened one.
 */
function closeQr(): void {
  clearQr();
  qrIndex = 0;
  if (qrTimer !== null) { clearInterval(qrTimer); qrTimer = null; }
}

function openQr(bytes: Uint8Array, source: 'to sign' | 'combined'): void {
  try {
    const frames = encodeFrames(bytes, state.qr.format).parts;
    state.qr.frames = frames;
    state.qr.open = true;
    state.qr.source = source;
    qrIndex = 0;
  } catch (e) {
    closeQr();
    state.loadError = `Could not build a QR for that PSBT: ${(e as Error).message}`;
  }
  render();
}

on(app, 'click', '[data-qr-open]', (el) => {
  const which = el.dataset.qrOpen;
  const bytes = which === 'combined' ? state.assembly.combined : state.loaded?.bytes;
  if (bytes) openQr(bytes, which === 'combined' ? 'combined' : 'to sign');
});
on(app, 'click', '[data-qr-format]', (el) => {
  // `switchQrFormat` owns the decision, and tears the panel down itself when there is nothing
  // left to re-encode — see the note on it. Null here means the frames are already gone.
  const bytes = switchQrFormat(el.dataset.qrFormat as Format);
  if (!bytes) {
    closeQr();
    state.loadError =
      'The transaction those codes were built from is no longer loaded, so they cannot be ' +
      'rebuilt in another format. Load the file again and reopen the codes.';
    render();
    return;
  }
  openQr(bytes, state.qr.source);
});
on(app, 'click', '#qr-close', () => { closeQr(); render(); });
on(app, 'click', '#qr-slower', () => { state.qr.interval = Math.min(2000, state.qr.interval + 150); paintQr(); });
on(app, 'click', '#qr-faster', () => { state.qr.interval = Math.max(100, state.qr.interval - 150); paintQr(); });

/* ── camera scanning ──────────────────────────────────────────────────────── */

let stream: MediaStream | null = null;
let scanRaf = 0;
const scanner = new Scanner();

function stopScan(): void {
  if (scanRaf) { cancelAnimationFrame(scanRaf); scanRaf = 0; }
  stream?.getTracks().forEach((t) => t.stop());
  stream = null;
  scanner.reset();
  const el = $('#scanner');
  if (el) el.hidden = true;
}

async function startScan(onResult: (bytes: Uint8Array) => void): Promise<void> {
  const shell = $('#scanner');
  const video = $<HTMLVideoElement>('#scan-video');
  const status = $('#scan-status');
  const detail = $('#scan-detail');
  const bar = $('#scan-progress');
  if (!shell || !video) return;
  shell.hidden = false;
  scanner.reset();

  if (status) status.textContent = hasNativeDetector()
    ? 'Using the browser\'s built-in QR detector.'
    : 'Using the bundled decoder — this browser has no BarcodeDetector.';

  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' },
    });
  } catch (e) {
    if (status) status.textContent =
      `Camera unavailable: ${(e as Error).message}. Load the PSBT from a file instead.`;
    return;
  }
  video.srcObject = stream;
  await video.play().catch(() => undefined);

  const detect = await makeDetector();
  const tick = async () => {
    if (!stream) return;
    try {
      for (const text of await detect(video)) {
        try {
          const done = scanner.accept(text);
          const p = scanner.progress();
          if (bar) bar.style.width = p.expected
            ? `${Math.min(100, (p.received / p.expected) * 100)}%` : '0%';
          /*
           * The bar can treat both formats alike — a ratio is a ratio. The WORDS cannot.
           * `${p.received} / ${p.expected}` rendered UR's percentage as "ur · 33 / 100", which
           * reads as frame 33 of 100; UR is a fountain code with no hundredth frame, and the
           * scan usually completes long before that number would be reached.
           */
          if (detail) detail.textContent = !p.format ? ''
            : p.unit === 'percent' ? `${p.format} · ${p.received}% assembled`
            : `${p.format} · ${p.received}${p.expected ? ` of ${p.expected}` : ''} frames`;
          if (done) { stopScan(); onResult(done); return; }
        } catch (err) {
          if (detail) detail.textContent = (err as Error).message;
        }
      }
    } catch { /* a dropped frame is normal; keep going */ }
    scanRaf = requestAnimationFrame(() => void tick());
  };
  void tick();
}

on(app, 'click', '[data-scan]', (el) => {
  const target = el.dataset.scan;
  void startScan((bytes) => {
    if (target === 'signature') addSig(bytes, 'scanned');
    else inspect(bytes);
  });
});
document.getElementById('scan-stop')?.addEventListener('click', stopScan);

/* ── Ledger over WebHID ───────────────────────────────────────────────────── */

let session: import('./ledger.js').LedgerSession | null = null;

on(app, 'click', '#ledger-connect', () => {
  void (async () => {
    state.ledger.error = null;
    state.ledger.message = 'Opening the device…';
    render();
    try {
      const { connect } = await import('./ledger.js');
      session = await connect(state.parsed!, 'Signing Room vault');
      state.ledger.connected = true;
      state.ledger.fingerprint = session.fingerprint;
      state.ledger.needsRegistration = session.needsRegistration;
      state.ledger.message = `Connected · ${session.fingerprint}`;
      // Already registered? Sign immediately — there is nothing else the user needs to decide.
      if (!session.needsRegistration) await ledgerSign();
    } catch (e) {
      state.ledger.message = null;
      state.ledger.error = (e as Error).message;
    }
    render();
  })();
});

on(app, 'click', '#ledger-register', () => {
  void (async () => {
    state.ledger.error = null;
    state.ledger.message = 'Confirm the policy on the device…';
    render();
    try {
      await session!.register();
      state.ledger.needsRegistration = false;
      state.ledger.message = 'Policy approved. Signing…';
      render();
      await ledgerSign();
    } catch (e) {
      state.ledger.error = (e as Error).message;
      state.ledger.message = null;
    }
    render();
  })();
});

async function ledgerSign(): Promise<void> {
  if (!session || !state.loaded) return;
  state.ledger.message = 'Confirm the transaction on the device…';
  render();
  const signed = await session.sign(state.loaded.bytes);
  // Straight into the same contribution path as a pasted or scanned PSBT — including the
  // intentHash check. A signature arriving over USB gets no more trust than one arriving by QR.
  addContribution(signed, `Ledger ${session.fingerprint}`);
  state.ledger.message = state.assembly.error ? null : 'Signature added.';
}

/* ── build identity ───────────────────────────────────────────────────────── */

/**
 * Show which build this is. Deliberately worded as an identity, not a guarantee.
 *
 * The hash is a constant stamped into the page at build time, so a tampered build would display
 * whatever it liked — this tells you WHICH build you are looking at, and nothing more. The page
 * cannot verify itself even in principle: `connect-src 'none'` means it cannot fetch its own
 * bundle to hash it, and that is the right trade. Verification is external, with `verify.sh`.
 */
function showBuild(): void {
  const el = $('#build-hash');
  const meta = document.querySelector<HTMLMetaElement>('meta[name="build-hash"]');
  if (!el) return;
  el.textContent = meta?.content
    ? `build ${meta.content.slice(0, 16)}… — reproducible; anyone can rebuild it and compare`
    : 'unstamped build (dev server)';
  if (meta?.content) el.title = meta.content;
}

/* ── clickjacking ─────────────────────────────────────────────────────────── */

/**
 * Refuse to run inside a frame. Done here because it cannot be done in the policy.
 *
 * CSP3 §3.3: a policy delivered in a <meta> element MUST have `frame-ancestors` ignored, and a
 * meta tag is the only policy Solo has — there is no server to set a response header. The
 * directive used to sit in the CSP and be asserted by a passing test, which is the worst of
 * both: no protection, and a green tick claiming there was.
 *
 * The failure mode this guards is specific. Framed, an attacker controls what surrounds the
 * page and can overlay it: the amount a user reads and the amount they approve stop being the
 * same number. So the page blanks itself and tries to break out.
 *
 * Say plainly what this is worth. A response header is strictly stronger — this runs only if
 * script runs, and an iframe sandboxed without `allow-top-navigation` can swallow the escape
 * (which is why the body is cleared FIRST: the blank page is the fallback defence, not the
 * navigation). It is what a header-less static bundle can actually enforce.
 */
function refuseToBeFramed(): boolean {
  let framed: boolean;
  // A cross-origin parent makes the comparison itself throw. That throw IS the answer.
  try { framed = window.top !== window.self; } catch { framed = true; }
  if (!framed) return false;

  document.body.textContent =
    'Signing Room refuses to run inside a frame. Open it in its own tab.';
  // Cross-origin `location` assignment is permitted; a sandboxed frame will throw instead.
  try { window.top!.location.href = window.self.location.href; } catch { /* blanked above */ }
  return true;
}

/* ── go ───────────────────────────────────────────────────────────────────── */

/**
 * Reveal the app. Called only when the framing check passed AND the script is actually running.
 *
 * The shell ships `hidden` because a sandboxed frame can stop this module executing at all — an
 * opaque origin fails the CORS check on Vite's `crossorigin` module script — and a page that
 * renders full Signing Room chrome with no code behind it is exactly what a clickjacker wants.
 * Failing to a plain "this has not started" message is the correct failure here: there is no
 * content being lost, only chrome for an app that is not running.
 */
function revealApp(): void {
  document.getElementById('shell')?.removeAttribute('hidden');
  document.getElementById('boot')?.remove();
}

if (!refuseToBeFramed()) {
  revealApp();
  loadVault();
  parseVault();
  /*
   * `/app/#check` opens on "I was sent a proof". The landing page's second CTA promises exactly
   * that. This has to run BEFORE the first render — it first lived at the bottom of the file,
   * after the boot sequence, so it set the mode on a screen that had already painted and the
   * link landed on the prove screen anyway: a promise the code agreed with and did not keep.
   */
  if (location.hash === '#check') state.control.mode = 'check';
  render();
  showBuild();
}

/* ── proof of wallet control ──────────────────────────────────────────────── */

/**
 * Read the control screen's free-text fields out of the DOM before acting.
 *
 * `render()` replaces innerHTML, so anything typed since the last render exists only in the live
 * elements. Every handler below reads first for the same reason the inspector does: acting on
 * `state` alone would silently use the value from before the user finished typing.
 */
function readControlInputs(): void {
  const C = state.control;
  const ta = (id: string): string | null => {
    const el = $(`#${id}`);
    return el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement ? el.value : null;
  };
  C.challengeText = ta('ctl-challenge') ?? C.challengeText;
  C.signature = ta('ctl-signature') ?? C.signature;
  C.checkText = ta('ctl-check') ?? C.checkText;
  C.address = ta('ctl-address') ?? C.address;
}

/** Everything downstream of the challenge, cleared at ONE owner when the challenge changes. */
function resetProof(): void {
  state.control.signature = '';
  state.control.proofJson = '';
  state.control.result = null;
}

on(app, 'click', '[data-ctl-mode]', (el) => {
  readControlInputs();
  state.control.mode = el.dataset.ctlMode === 'check' ? 'check' : 'prove';
  state.control.error = null;
  // The verdict belongs to the box it came from. Carrying it across would show a verdict about
  // someone else's proof on the screen where you are building your own.
  state.control.result = null;
  render();
});

on(app, 'click', '#ctl-use-challenge', () => {
  readControlInputs();
  const C = state.control;
  try {
    C.challenge = readChallenge(C.challengeText);
    C.message = messageToSign(C.challenge);
    C.selfIssued = false;
    C.error = null;
    resetProof();
  } catch (e) {
    C.challenge = null; C.message = ''; C.error = (e as Error).message;
  }
  render();
});

on(app, 'click', '#ctl-issue', () => {
  readControlInputs();
  const C = state.control;
  if (!state.parsed) { C.error = 'Set up the wallet descriptor first.'; render(); return; }
  try {
    C.challenge = issueChallenge({
      // The descriptor checksum IS the wallet's identity to a counterparty. A product-shaped
      // prefix in a protocol artifact somebody else reads is branding where evidence belongs.
      vaultId: state.parsed.checksum,
      descriptorChecksum: state.parsed.checksum,
      challenge: 'Self-issued from Signing Room Solo',
      now: Date.now(),
    });
    C.challengeText = JSON.stringify(C.challenge, null, 2);
    C.message = messageToSign(C.challenge);
    C.selfIssued = true;       // the UI says out loud that this is the weaker claim
    C.error = null;
    resetProof();
  } catch (e) { C.error = (e as Error).message; }
  render();
});

on(app, 'click', '#ctl-build', () => {
  readControlInputs();
  const C = state.control;
  if (!C.challenge) { C.error = 'Load a challenge first.'; render(); return; }
  if (!state.parsed) { C.error = 'Set up the wallet descriptor first.'; render(); return; }
  try {
    C.proofJson = proofToJson(assembleProof(state.vault.descriptor.trim(), state.parsed,
      scanWindow(), C.challenge, [{ signature: C.signature, address: C.address }]));
    C.error = null;
    // Checked the moment it exists, through the SAME verifier a stranger runs. "Send this" is
    // rendered only on a passing verdict; a failing one shows its reason and no copy button.
    C.result = checkProof(C.proofJson, Date.now());
  } catch (e) { C.proofJson = ''; C.error = (e as Error).message; }
  render();
});

on(app, 'click', '#ctl-verify', () => {
  readControlInputs();
  state.control.result = checkProof(state.control.checkText, Date.now());
  state.control.error = null;
  render();
});

/*
 * "Check it myself first" runs the proof you just built through the SAME verifier a stranger
 * would use — not a friendlier internal path. A prover who cannot see the recipient's answer
 * finds out it failed from the recipient.
 */
on(app, 'click', '#ctl-selfcheck', () => {
  readControlInputs();
  state.control.result = checkProof(state.control.proofJson, Date.now());
  render();
});

const copy = (text: string) => { void navigator.clipboard?.writeText(text); };
on(app, 'click', '#ctl-copy-message', () => copy(state.control.message));
on(app, 'click', '#ctl-copy-proof', () => copy(state.control.proofJson));
on(app, 'click', '#ctl-download-proof', () => {
  download(state.control.proofJson, 'control-proof.json', 'application/json');
});

/*
 * The screen tabs live OUTSIDE #app — `render()` replaces the innerHTML of #app, so a listener
 * bound there would never see them. Bound once, to the nav itself.
 */
const tabs = $('nav.tabs');
if (tabs) {
  on(tabs, 'click', '[data-tab]', (el) => {
    const next = el.dataset.tab === 'control' ? 'control' : 'inspect';
    if (next === state.tab) return;
    state.tab = next;
    for (const b of $$('nav.tabs [data-tab]'))
      b.setAttribute('aria-selected', String(b.dataset.tab === next));
    render();
  });
}
